import React from 'react'

import { Box, Skeleton } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { CardholderInformationQuery, NewCardLegalEntityFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import CardsNotAvailableModal from 'modules/expenses/components/CardsNotAvailableModal'
import LegalEntityAccountCreationModal from 'modules/expenses/components/LegalEntityAccountCreationModal'
import { AccountAlert } from 'modules/expenses/components/cards/PageContents/AccountAlert'

import { CardholderForm } from './CardholderForm'
import SettingsForm from './SettingsForm'
import { ShippingForm } from './ShippingForm'
import { NewCardUserSelectorForm } from './UserSelector'
import { CreateCardForm } from './useCardForm'

const NewCardSkeleton = () => {
  return (
    <Box flexDirection="column" gap="s32">
      <Box flexDirection="column" gap="s8">
        <Box flexDirection="row" gap="s0">
          <Box flexDirection="column" gap="s4">
            <Box paddingY={'s2'}>
              <Skeleton width="s300" height="s16" />
            </Box>
            <Skeleton width="s300" height="s40" />
          </Box>
          <Box flexDirection="column" gap="s4">
            <Box paddingY={'s2'}>
              <Skeleton width="s300" height="s16" />
            </Box>
            <Skeleton width="s300" height="s40" />
          </Box>
        </Box>
        <Box paddingY={'s2'}>
          <Skeleton width="s400" height="s16" />
        </Box>
      </Box>
      <Box gap="s16" flexDirection="row">
        <Skeleton width="s200" height="s12" />
        <Skeleton width="s200" height="s12" />
      </Box>
      <Skeleton width="s400" height="s12" />
    </Box>
  )
}

type Props = {
  loadingCardholder: boolean
  form: FormikProps<CreateCardForm>
  mainLegalEntity?: NewCardLegalEntityFragment
  selectedLegalEntity?: NewCardLegalEntityFragment | null
  hasAccount: boolean
  cardsAvailableInCountry: boolean
  selectedEmployeeInformation?: CardholderInformationQuery['employees']['employee']
}

export const CardForm = ({
  loadingCardholder,
  form,
  hasAccount,
  selectedLegalEntity,
  cardsAvailableInCountry,
  selectedEmployeeInformation,
}: Props) => {
  const locale = useCurrentSession().access.locale
  const selectedLegalEntityOption = {
    id: selectedLegalEntity?.id,
    legalName: selectedLegalEntity?.legalName,
    main: selectedLegalEntity?.main,
  }
  const currency = selectedLegalEntity?.currency || selectedEmployeeInformation?.company.currency
  const showCardsNotAvailableModal =
    !hasAccount &&
    selectedLegalEntity?.id &&
    selectedEmployeeInformation?.id &&
    cardsAvailableInCountry === false
  const showAccountNotCompletedModal =
    !hasAccount &&
    selectedLegalEntity?.id &&
    selectedEmployeeInformation?.id &&
    cardsAvailableInCountry === true

  return (
    <>
      <Box
        gap="s64"
        width="full"
        maxWidth="s600"
        alignItems="stretch"
        justifyContent="center"
        paddingTop="s80"
      >
        <Box gap="s32" width="full">
          <NewCardUserSelectorForm form={form} />
          {selectedLegalEntity && (
            <AccountAlert
              hasEnabledAccount={hasAccount}
              cardsAvailable={cardsAvailableInCountry}
              legalEntity={selectedLegalEntity}
            />
          )}
          {loadingCardholder && <NewCardSkeleton />}
          {!loadingCardholder &&
            form.values.employeeId &&
            hasAccount &&
            !selectedEmployeeInformation?.bankingCardholder && (
              <CardholderForm form={form} locale={locale} />
            )}
          {selectedEmployeeInformation && hasAccount && (
            <>
              {form.values.cardType == 'physical' && <ShippingForm form={form} />}
              {currency && <SettingsForm form={form} currency={currency} />}
            </>
          )}
        </Box>
      </Box>
      {showAccountNotCompletedModal && selectedLegalEntityOption?.id && (
        <LegalEntityAccountCreationModal
          legalEntity={{ ...selectedLegalEntityOption, id: selectedLegalEntityOption.id }}
        />
      )}
      {showCardsNotAvailableModal && <CardsNotAvailableModal onClose={() => true} />}
    </>
  )
}
